import React from 'react';
import SEO from '../components/SEO';
import PageHero, { CTABanner } from '../components/PageHero';
import { industries } from '../data/mock';
import {
  Landmark, Building2, HeartHandshake, Banknote, Ship, Cpu, Factory, FlaskConical,
} from 'lucide-react';

const iconMap = { Landmark, Building2, HeartHandshake, Banknote, Ship, Cpu, Factory, FlaskConical };

export default function IndustriesPage() {
  return (
    <>
      <SEO
        title="Industries We Serve"
        description="Sector fluency across government, private enterprise, banking, NGOs, technology, energy, logistics, and healthcare — calibrated to the specific dynamics of each industry."
        path="/industries"
        keywords="industries served, government advisory, banking compliance, NGO consulting, technology firms, healthcare advisory, logistics consulting, energy sector"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Industries — Salihi Consulting',
          url: 'https://salihiconsulting.com/industries',
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: industries.map((ind, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              item: { '@type': 'Thing', name: ind.name, description: ind.long },
            })),
          },
        }}
      />
      <PageHero
        breadcrumb="Industries"
        eyebrow="Sector fluency"
        title="Sector fluency across the"
        italicTitle="economy that matters most."
        subtitle="Every industry runs on its own grammar of regulation, capital, talent and risk. Our engagements are calibrated to the specific dynamics of each sector — informed by years of work alongside the institutions that shape them."
      />

      {/* Industry detail blocks */}
      <section className="py-20 sm:py-24 bg-[#0d2143]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 space-y-8">
          {industries.map((ind, idx) => {
            const Icon = iconMap[ind.icon] || Building2;
            const reversed = idx % 2 === 1;
            return (
              <article key={ind.name} className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-3xl p-7 sm:p-9 lg:p-11 hover:border-[#e8b863]/35 transition-colors">
                <div className={`lg:col-span-4 flex justify-center ${reversed ? 'lg:order-2' : ''}`}>
                  <div className="relative">
                    <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-3xl bg-gradient-to-br from-[#122a52] to-[#1a3a6b] border border-[#e8b863]/30 flex items-center justify-center shadow-2xl shadow-black/40">
                      <Icon className="w-12 h-12 sm:w-14 sm:h-14 text-[#e8b863]" strokeWidth={1.5} />
                    </div>
                    <div className="absolute -inset-2 rounded-3xl border border-[#e8b863]/15" />
                  </div>
                </div>
                <div className={`lg:col-span-8 ${reversed ? 'lg:order-1' : ''}`}>
                  <p className="text-[10px] text-[#e8b863] tracking-[0.22em] uppercase font-semibold mb-3">Industry</p>
                  <h2 className="font-serif text-white font-medium text-[26px] sm:text-[32px] lg:text-[36px] leading-[1.12] tracking-tight">
                    {ind.name}
                  </h2>
                  <p className="mt-5 text-white/78 text-[15px] lg:text-[16px] leading-relaxed">
                    {ind.long}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <CTABanner
        title="Operating in a sector not listed here?"
        subtitle="Our methodology is industry-agnostic. Tell us about your context and we'll demonstrate where we can add value."
      />
    </>
  );
}
